import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { findDuplicateCustomers, DuplicateCustomerGroup, Customer, mergeCustomers } from '../../../firebase/services/customerVesselService';

const DuplicateCustomersPage: React.FC = () => {
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateCustomerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateCustomerGroup | null>(null);
  const [primaryCustomer, setPrimaryCustomer] = useState<Customer | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDuplicateCustomers();
  }, []);

  const loadDuplicateCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const groups = await findDuplicateCustomers();
      setDuplicateGroups(groups);
    } catch (err) {
      console.error('Error loading duplicate customers:', err);
      setError('Failed to load duplicate customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMergeClick = (group: DuplicateCustomerGroup) => {
    setSelectedGroup(group);
    setPrimaryCustomer(group.customers[0]);
    setMergeDialogOpen(true);
  };

  const handleMerge = async () => {
    if (!selectedGroup || !primaryCustomer) return;

    try {
      setLoading(true);
      setError(null);

      // Get all customer IDs except the primary one
      const customerIdsToMerge = selectedGroup.customers
        .filter(c => c.id !== primaryCustomer.id)
        .map(c => c.id);

      // Merge the customers
      await mergeCustomers(primaryCustomer.id, customerIdsToMerge);

      // Close dialog and refresh the list
      setMergeDialogOpen(false);
      await loadDuplicateCustomers();
    } catch (err) {
      console.error('Error merging customers:', err);
      setError('Failed to merge customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && duplicateGroups.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Duplicate Customers
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/admin/business/customers')}
        >
          Back to Customers
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {duplicateGroups.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No duplicate customers found.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Match Type</TableCell>
                <TableCell>Match Value</TableCell>
                <TableCell>Customers</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {duplicateGroups.map((group, index) => (
                <TableRow key={`${group.matchType}-${group.matchValue}-${index}`}>
                  <TableCell>
                    <Chip
                      label={group.matchType.charAt(0).toUpperCase() + group.matchType.slice(1)}
                      color={
                        group.matchType === 'email' ? 'primary' :
                        group.matchType === 'phone' ? 'secondary' : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>{group.matchValue}</TableCell>
                  <TableCell>
                    {group.customers.map(customer => (
                      <Box key={customer.id} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                        </Typography>
                        {customer.email && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {customer.email}
                          </Typography>
                        )}
                        {customer.phone && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {customer.phone}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleMergeClick(group)}
                    >
                      Merge
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Merge Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Merge Duplicate Customers</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Select the primary customer record to keep. All other records will be merged into this one.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Primary Customer</InputLabel>
                <Select
                  value={primaryCustomer?.id || ''}
                  onChange={(e) => {
                    const customer = selectedGroup?.customers.find(c => c.id === e.target.value);
                    if (customer) setPrimaryCustomer(customer);
                  }}
                  label="Primary Customer"
                >
                  {selectedGroup?.customers.map(customer => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                      {customer.email && ` (${customer.email})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleMerge}
            variant="contained"
            color="primary"
            disabled={!primaryCustomer}
          >
            Merge Customers
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DuplicateCustomersPage; 